/* ==========================================================================
   Per-provider setting overrides (PORTAL-72)
   Labels for each overridable setting, shared between the Override Settings
   page and the overrides summary on the provider details page (PORTAL-74).
   ========================================================================== */

import type { ProviderSettingsResponse, ProviderSettingsValues } from '@/api/types'
import { isCanada } from '@/utils/countries'

export type SettingKey = keyof ProviderSettingsValues

export interface SettingMeta {
  key: SettingKey
  label: string
  description: string
}

export const GENERAL_SETTINGS: SettingMeta[] = [
  {
    key: 'signature_required',
    label: 'Require Provider Signature',
    description: 'Require an e-signature to submit a prescription. When off, signing is optional.',
  },
  {
    key: 'npi_required',
    label: 'NPI Required',
    description: "Require the provider to enter their NPI to complete their profile.",
  },
  {
    key: 'dea_required',
    label: 'DEA Required',
    description: "Require the provider to enter their DEA number to complete their profile.",
  },
  {
    key: 'phone_required',
    label: 'Phone Required',
    description: 'Require the provider to enter their phone number to complete their profile.',
  },
  {
    key: 'patient_address_required',
    label: 'Patient Address Required',
    description: 'Require a patient address to submit a prescription (human patients only).',
  },
  {
    key: 'provider_address_lock',
    label: 'Manage Provider Addresses',
    description:
      "When on, the pharmacy manages this provider's practice addresses and the provider cannot edit them.",
  },
  {
    key: 'allow_country_field',
    label: 'Country Field',
    description: 'Expose a country field on the provider practice-address editor.',
  },
]

export const PAYMENT_DELIVERY_SETTINGS: SettingMeta[] = [
  {
    key: 'hide_payment_delivery_section',
    label: 'Hide Payment & Delivery Section',
    description: 'Hide the entire Payment & Delivery section on the prescription form.',
  },
  {
    key: 'hide_diagnosis_code',
    label: 'Hide Diagnosis Code',
    description: 'Hide the ICD-10 diagnosis code field.',
  },
  {
    key: 'hide_payment_type',
    label: 'Hide Payment Type',
    description: 'Hide the payment type field.',
  },
  {
    key: 'hide_delivery_method',
    label: 'Hide Delivery Method',
    description: 'Hide the delivery method field.',
  },
]

// The Payment & Delivery child settings governed by the
// `hide_payment_delivery_section` parent. When the section is hidden these are
// forced hidden by the backend cascade, so their controls become a no-op and are
// locked in the UI (mirrors the pharmacy Settings page).
export const PAYMENT_DELIVERY_CHILD_KEYS: SettingKey[] = [
  'hide_diagnosis_code',
  'hide_payment_type',
  'hide_delivery_method',
]

// Payment & Delivery field requirements (PORTAL-202).
export const PAYMENT_DELIVERY_REQUIRED_SETTINGS: SettingMeta[] = [
  {
    key: 'diagnosis_code_required',
    label: 'Require Diagnosis Code',
    description: 'Require an ICD-10 diagnosis code to submit a prescription.',
  },
  {
    key: 'payment_type_required',
    label: 'Require Payment Type',
    description: 'Require a payment type to submit a prescription.',
  },
  {
    key: 'delivery_method_required',
    label: 'Require Delivery Method',
    description: 'Require a delivery method to submit a prescription.',
  },
]

// Each requirement mapped to the hide flag of the field it governs. A hidden
// field can't be filled, so the backend resolves its requirement to Off and the
// UI locks the control while the field is hidden.
export const REQUIRED_FIELD_HIDE_KEY: Partial<Record<SettingKey, SettingKey>> = {
  diagnosis_code_required: 'hide_diagnosis_code',
  payment_type_required: 'hide_payment_type',
  delivery_method_required: 'hide_delivery_method',
}

// State per setting: null = inherit portal default; true/false = explicit override.
export type OverrideState = Record<SettingKey, boolean | null>

const SETTING_KEYS: SettingKey[] = [
  ...GENERAL_SETTINGS.map((s) => s.key),
  ...PAYMENT_DELIVERY_SETTINGS.map((s) => s.key),
  ...PAYMENT_DELIVERY_REQUIRED_SETTINGS.map((s) => s.key),
]

export function overridesFromResponse(res: ProviderSettingsResponse): OverrideState {
  const state = {} as OverrideState
  for (const key of SETTING_KEYS) {
    state[key] = res[`${key}_override` as keyof ProviderSettingsResponse] as
      | boolean
      | null
  }
  return state
}

/** The general settings that apply to a pharmacy's country — Canadian
 *  pharmacies have no NPI / DEA (PORTAL-161). */
export function generalSettingsFor(country: string | null | undefined): SettingMeta[] {
  return isCanada(country)
    ? GENERAL_SETTINGS.filter((s) => s.key !== 'npi_required' && s.key !== 'dea_required')
    : GENERAL_SETTINGS
}
